import { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';

// Resolve a mand occurrence's display name. The current data-collection form
// (DailyMandMode) writes `materialName` + `targetName`; older/offline entries
// used `mandName` / `itemName` / `name`. Fall through so legacy data still
// renders a label instead of a blank cell.
const mandNameOf = (o) => o.materialName || o.mandName || o.itemName || o.name || '—';

// "independent" is surfaced to therapists as "Unprompted" everywhere in the
// data-collection UI; mirror that terminology here.
const typeLabelOf = (type) => (type === 'independent' ? 'Unprompted' : type === 'prompted' ? 'Prompted' : type || '—');

const tsValue = (o) => {
  const t = o.timestamp ? new Date(o.timestamp).getTime() : NaN;
  return Number.isNaN(t) ? null : t;
};

// Grouped, expandable Daily Mand detail. Shows one row per mand with its
// prompted / unprompted counts; expanding a row reveals each occurrence's
// timing (and note, if any). Shared by the therapist and supervisor calendars.
export default function DailyMandDetail({ raw }) {
  const theme = useTheme();
  // Custom-mand accent, derived from the Ayro palette primary token (no hardcoded
  // hex) — matches DailyMandMode so custom mands look the same everywhere.
  const customMandBorder = theme.palette.primary.main;
  const customMandTint = alpha(theme.palette.primary.main, 0.04);
  const occurrences = useMemo(() => (Array.isArray(raw?.occurrences) ? raw.occurrences : []), [raw]);
  const [expanded, setExpanded] = useState({});

  const groups = useMemo(() => {
    const map = new Map();
    occurrences.forEach((o) => {
      const name = mandNameOf(o);
      const key = o.key || `${name}__${o.targetName || ''}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          mandName: name,
          targetName: o.targetName && o.targetName !== name ? o.targetName : null,
          prompted: 0,
          independent: 0,
          items: []
        });
      }
      const g = map.get(key);
      if (o.type === 'independent') g.independent += 1;
      else if (o.type === 'prompted') g.prompted += 1;
      g.items.push(o);
    });
    const list = Array.from(map.values());
    // Chronological order within a mand (oldest → newest); fall back to the
    // original insertion order when timestamps are missing/unparseable.
    list.forEach((g) =>
      g.items.sort((a, b) => {
        const ta = tsValue(a);
        const tb = tsValue(b);
        if (ta == null || tb == null) return 0;
        return ta - tb;
      })
    );
    return list;
  }, [occurrences]);

  // Ad-hoc custom mands recorded this session. Stored separately from the
  // programme summary, so they're surfaced here as their own (non-expandable)
  // rows and folded into the header chips — but never mixed into target data.
  const customGroups = useMemo(() => {
    const list = Array.isArray(raw?.customMands) ? raw.customMands : [];
    return list.map((m, idx) => {
      /* Occurrence times, when the session recorded any. Custom mands were once
       * counters alone, so an older entry has no `occurrences` key at all — an
       * empty list here means "not recorded", and the row stays flat rather than
       * offering a drill-down onto nothing. */
      const items = (Array.isArray(m.occurrences) ? m.occurrences : []).slice().sort((a, b) => {
        const ta = tsValue(a);
        const tb = tsValue(b);
        if (ta == null || tb == null) return 0;
        return ta - tb;
      });
      return {
        key: m.id || `custom-${idx}`,
        mandName: (m.name || '').trim() || '—',
        prompted: Number(m.prompted) || 0,
        independent: Number(m.independent) || 0,
        items
      };
    });
  }, [raw]);

  const customPrompted = customGroups.reduce((s, g) => s + g.prompted, 0);
  const customIndependent = customGroups.reduce((s, g) => s + g.independent, 0);

  // Prefer the form's saved totals; fall back to counts derived from groups so
  // the chips stay correct even for data saved without summary fields. Custom
  // mand counts are added on top so the session view reflects everything.
  const derivedPrompted = groups.reduce((s, g) => s + g.prompted, 0);
  const derivedIndependent = groups.reduce((s, g) => s + g.independent, 0);
  const total = (raw?.totalMands ?? occurrences.length) + customPrompted + customIndependent;
  const prompted = (raw?.promptedMands ?? derivedPrompted) + customPrompted;
  const independent = (raw?.independentMands ?? derivedIndependent) + customIndependent;

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={`Total ${total}`} />
        <Chip size="small" color="success" label={`Unprompted ${independent}`} />
        <Chip size="small" color="warning" label={`Prompted ${prompted}`} />
      </Stack>

      {(groups.length > 0 || customGroups.length > 0) && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Mand</TableCell>
                <TableCell align="center" width={110}>
                  Prompted
                </TableCell>
                <TableCell align="center" width={110}>
                  Unprompted
                </TableCell>
                <TableCell align="center" width={80}>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => {
                const isOpen = !!expanded[g.key];
                const groupTotal = g.prompted + g.independent;
                return (
                  <Fragment key={g.key}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer', '& > td': { borderBottom: isOpen ? 'unset' : undefined } }}
                      onClick={() => toggle(g.key)}
                    >
                      <TableCell>
                        <IconButton size="small" aria-label={isOpen ? 'Collapse' : 'Expand'}>
                          {isOpen ? <UpOutlined style={{ fontSize: '0.7rem' }} /> : <DownOutlined style={{ fontSize: '0.7rem' }} />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {g.mandName}
                        </Typography>
                        {g.targetName && (
                          <Typography variant="caption" color="text.secondary">
                            {g.targetName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" color="warning" variant={g.prompted ? 'filled' : 'outlined'} label={g.prompted} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" color="success" variant={g.independent ? 'filled' : 'outlined'} label={g.independent} />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={700}>
                          {groupTotal}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5, pl: { xs: 2, sm: 6 } }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Occurrence times ({g.items.length})
                            </Typography>
                            <Stack spacing={0.75}>
                              {g.items.map((o, idx) => (
                                <Stack key={idx} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <ClockCircleOutlined style={{ fontSize: '0.8rem', opacity: 0.6 }} />
                                  <Typography variant="body2" sx={{ minWidth: 84 }}>
                                    {o.time || '—'}
                                  </Typography>
                                  <Chip size="small" color={o.type === 'independent' ? 'success' : 'warning'} label={typeLabelOf(o.type)} />
                                  {o.notes && (
                                    <Typography variant="caption" color="text.secondary">
                                      {o.notes}
                                    </Typography>
                                  )}
                                </Stack>
                              ))}
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
              {/* Ad-hoc custom mands, with the custom left-border accent so they
                  read as distinct from target-driven mands.

                  Expandable ONLY when the entry carries occurrence times. A custom
                  mand used to be a pair of counters with no timings at all, so any
                  session recorded before that changed has counts and nothing to
                  drill into — those rows stay flat rather than offering a chevron
                  that opens an empty drawer. */}
              {customGroups.map((g) => {
                const groupTotal = g.prompted + g.independent;
                const canExpand = g.items.length > 0;
                const isOpen = canExpand && Boolean(expanded[g.key]);
                return (
                  <Fragment key={g.key}>
                    <TableRow
                      hover
                      onClick={canExpand ? () => setExpanded((prev) => ({ ...prev, [g.key]: !prev[g.key] })) : undefined}
                      sx={{
                        bgcolor: customMandTint,
                        cursor: canExpand ? 'pointer' : 'default',
                        '& > td:first-of-type': { borderLeft: `4px solid ${customMandBorder}` },
                        '& > td': { borderBottom: isOpen ? 'none' : undefined }
                      }}
                    >
                      <TableCell>
                        {canExpand && (
                          <IconButton size="small" aria-label={isOpen ? 'Collapse' : 'Expand'}>
                            {isOpen ? <UpOutlined style={{ fontSize: '0.7rem' }} /> : <DownOutlined style={{ fontSize: '0.7rem' }} />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {g.mandName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: customMandBorder }}>
                          Custom
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" color="warning" variant={g.prompted ? 'filled' : 'outlined'} label={g.prompted} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" color="success" variant={g.independent ? 'filled' : 'outlined'} label={g.independent} />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={700}>
                          {groupTotal}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {canExpand && (
                      <TableRow sx={{ bgcolor: customMandTint }}>
                        <TableCell
                          colSpan={5}
                          sx={{
                            py: 0,
                            borderBottom: isOpen ? undefined : 'none',
                            borderLeft: `4px solid ${customMandBorder}`
                          }}
                        >
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, pl: { xs: 2, sm: 6 } }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Occurrence times ({g.items.length})
                              </Typography>
                              <Stack spacing={0.75}>
                                {g.items.map((o, idx) => (
                                  <Stack key={o.id || idx} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <ClockCircleOutlined style={{ fontSize: '0.8rem', opacity: 0.6 }} />
                                    <Typography variant="body2" sx={{ minWidth: 84 }}>
                                      {o.time || '—'}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      color={o.type === 'independent' ? 'success' : 'warning'}
                                      label={typeLabelOf(o.type)}
                                    />
                                  </Stack>
                                ))}
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

DailyMandDetail.propTypes = {
  raw: PropTypes.shape({
    totalMands: PropTypes.number,
    promptedMands: PropTypes.number,
    independentMands: PropTypes.number,
    occurrences: PropTypes.array,
    customMands: PropTypes.array
  })
};
